import { useMemo, useState } from 'react';

import { AppIcon, BadgeCircle, CheckboxGroup } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useGetTestDatesQuery } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.api';
import {
  FOCUS_PREF_VALUES,
  HOURS_PER_DAY,
  normalizeDraftStartDate,
  resolveDraftStartDate,
  WEEKDAYS,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.constants';
import {
  addBlackout,
  removeBlackout,
  selectBoosterTargets,
  setStudyDays,
  updateTargets,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import {
  FocusPref,
  ReportFacts,
  TargetTest,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import {
  Box,
  Button,
  Chip,
  Divider,
  Slider,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';

import { BrandDatePicker } from '../components/BrandDatePicker';
import { InsufficientScheduleCallout } from '../components/InsufficientScheduleCallout';
import { SectionCard, SectionGroup } from '../components/SectionCard';
import {
  stepChipRowSx,
  stepContainerSx,
  stepDividerSx,
  stepErrorSx,
  stepFieldColumnSx,
  stepIntroBodySx,
  stepMutedSx,
  stepNotSavedSx,
  stepTitleSx,
} from '../satActBooster.styles';
import {
  estimatePlanCoverage,
  isTestDateInRange,
  startDateBounds,
  testDateBounds,
} from '../utils/coverageEstimate';
import { longDate, testDateLabel } from '../utils/format';
import { getLocalInsufficientReasons } from '../utils/insufficientSchedule';
import { getTargetsErrors } from '../utils/stepNavigation';

const STRINGS = appStrings.modules.satActBooster;

type ScheduleStepProps = Readonly<{
  reportFacts: ReportFacts | null;
  /** True while the run has not yet been persisted, so the notice can say so. */
  saveDeferred?: boolean;
  /** Reveal all required-field errors once the user has tried to build the plan. */
  submitAttempted: boolean;
}>;

export const ScheduleStep = ({
  reportFacts,
  submitAttempted,
  saveDeferred = false,
}: ScheduleStepProps) => {
  const dispatch = useAppDispatch();
  const targets = useAppSelector(selectBoosterTargets);
  const resolvedTest = reportFacts?.test ?? targets.targetTest;

  const coverage = useMemo(() => estimatePlanCoverage(targets), [targets]);
  // Live light-schedule warning, recomputed from the draft on every field edit.
  const insufficientReasons = useMemo(
    () => getLocalInsufficientReasons(targets, coverage),
    [targets, coverage],
  );
  const { data: testDates = [] } = useGetTestDatesQuery(resolvedTest as TargetTest, {
    skip: !resolvedTest,
  });
  // Only offer dates the field itself would accept — a past sitting is not a pickable suggestion.
  const suggestedDates = testDates.filter((date) => isTestDateInRange(date.date));

  const [dayOff, setDayOff] = useState('');
  const [dayOffReason, setDayOffReason] = useState('');
  // The date arrives empty; hold its required-error until the field has been touched.
  const [testDateTouched, setTestDateTouched] = useState(false);

  const errors = getTargetsErrors(targets);
  // Recomputed per render rather than memoized, so a session left open across midnight doesn't
  // keep offering a day that has since passed.
  const testDateRange = testDateBounds();
  const startDateRange = startDateBounds(targets.testDate);
  // A picked-but-rejected date failed the window; an empty one is simply missing.
  const testDateHelperText = targets.testDate
    ? STRINGS.targets.testDateOutOfRange
    : STRINGS.targets.testDateRequired;
  // With no test date the ceiling is the plan's own span limit, so it can't be named against one.
  const startDateHelperText = targets.testDate
    ? STRINGS.targets.startDateOutOfRange
    : STRINGS.targets.startDateOutOfWindow;

  return (
    <Box sx={stepContainerSx}>
      {saveDeferred && (
        <Typography
          variant="text-sm-regular"
          sx={stepNotSavedSx}
          id="booster-schedule-not-saved-notice"
        >
          {STRINGS.report.notSaved}
        </Typography>
      )}
      <SectionCard>
        <Box>
          <Typography variant="text-lg-semibold" sx={stepTitleSx}>
            {STRINGS.targets.scheduleTitle}
          </Typography>
          <Typography variant="text-sm-regular" sx={stepIntroBodySx}>
            {STRINGS.targets.scheduleBody}
          </Typography>
        </Box>

        <SectionGroup label={STRINGS.targets.testDate}>
          <BrandDatePicker
            id="booster-target-test-date"
            value={targets.testDate ?? null}
            onChange={(next) => dispatch(updateTargets({ testDate: next }))}
            onBlur={() => setTestDateTouched(true)}
            min={testDateRange.min}
            max={testDateRange.max}
            markedDates={suggestedDates.map((date) => date.date)}
            error={(testDateTouched || submitAttempted) && errors.testDate}
            helperText={testDateHelperText}
          />
          {suggestedDates.length > 0 && (
            <Box sx={stepChipRowSx}>
              <Typography variant="text-sm-regular" sx={stepMutedSx}>
                {STRINGS.targets.upcomingDates((resolvedTest ?? '').toUpperCase())}
              </Typography>
              {suggestedDates.map((date) => (
                <Chip
                  key={date.date}
                  id={`booster-test-date-${date.date}`}
                  label={testDateLabel(date)}
                  size="small"
                  onClick={() => dispatch(updateTargets({ testDate: date.date }))}
                  sx={dateChipSx}
                />
              ))}
            </Box>
          )}
        </SectionGroup>

        <Divider sx={stepDividerSx} />

        <SectionGroup
          label={STRINGS.targets.startDate}
          supportingText={STRINGS.targets.startDatePrompt}
        >
          <BrandDatePicker
            id="booster-study-start-date"
            value={resolveDraftStartDate(targets.startDate)}
            onChange={(next) =>
              dispatch(updateTargets({ startDate: normalizeDraftStartDate(next) }))
            }
            min={startDateRange.min}
            max={startDateRange.max}
            error={errors.startDate}
            helperText={startDateHelperText}
          />
        </SectionGroup>

        <Divider sx={stepDividerSx} />

        <SectionGroup label={STRINGS.targets.hours}>
          <Typography variant="text-sm-regular" sx={stepMutedSx}>
            {STRINGS.targets.hoursPrompt}
          </Typography>
          <Box sx={sliderRowSx}>
            <Slider
              id="booster-hours-per-day"
              aria-label={STRINGS.targets.hoursAria}
              value={targets.hoursPerDay}
              min={HOURS_PER_DAY.min}
              max={HOURS_PER_DAY.max}
              step={HOURS_PER_DAY.step}
              marks
              onChange={(_event, value) =>
                dispatch(updateTargets({ hoursPerDay: value as number }))
              }
              sx={sliderSx}
            />
            <Typography variant="text-md-semibold" sx={hoursLabelSx}>
              {targets.hoursPerDay} {STRINGS.targets.hoursUnit}
            </Typography>
          </Box>
        </SectionGroup>

        <Divider sx={stepDividerSx} />

        <SectionGroup label={STRINGS.targets.focusPref}>
          <Box sx={stepFieldColumnSx}>
            <CheckboxGroup
              id="booster-focus-pref"
              options={FOCUS_PREF_VALUES.map((value) => ({
                value,
                label: STRINGS.focusPrefOptions[value].label,
              }))}
              selectedValues={[targets.focusPref]}
              setSelectedValues={(values) => {
                // Single-select: keep the newly toggled value; unchecking the only choice is a no-op.
                const next = values.find((value) => value !== targets.focusPref);

                if (next) dispatch(updateTargets({ focusPref: next as FocusPref }));
              }}
            />
          </Box>
          <Typography variant="text-sm-regular" sx={stepMutedSx}>
            {STRINGS.focusPrefOptions[targets.focusPref].helper}
          </Typography>
        </SectionGroup>

        <Divider sx={stepDividerSx} />

        <SectionGroup
          label={STRINGS.targets.studyDays}
          supportingText={STRINGS.targets.studyDaysPrompt}
        >
          <Box
            sx={dayPickerSx}
            id="booster-study-days"
            role="group"
            aria-label={STRINGS.targets.studyDays}
          >
            {WEEKDAYS.map((day) => {
              const selected = targets.studyDays.includes(day);

              return (
                <Box
                  key={day}
                  component="button"
                  type="button"
                  id={`booster-study-day-${day}`}
                  aria-pressed={selected}
                  aria-label={STRINGS.targets.weekdayLabels[day]}
                  onClick={() =>
                    dispatch(
                      setStudyDays(
                        selected
                          ? targets.studyDays.filter((value) => value !== day)
                          : [...targets.studyDays, day],
                      ),
                    )
                  }
                  sx={dayToggleSx(selected)}
                >
                  {STRINGS.targets.weekdayInitials[day]}
                </Box>
              );
            })}
          </Box>
          {submitAttempted && errors.studyDays && (
            <Typography variant="text-sm-regular" sx={stepErrorSx}>
              {STRINGS.targets.studyDaysRequired}
            </Typography>
          )}
        </SectionGroup>

        <Divider sx={stepDividerSx} />

        <SectionGroup
          label={STRINGS.targets.daysOff}
          supportingText={STRINGS.targets.daysOffPrompt}
        >
          <Box sx={dayOffRowSx}>
            <BrandDatePicker
              id="booster-day-off-date"
              value={dayOff || null}
              onChange={(next) => setDayOff(next ?? '')}
              fullWidth
              sx={dayOffFieldSx}
            />
            <TextField
              id="booster-day-off-reason"
              placeholder={STRINGS.targets.dayOffReasonPlaceholder}
              value={dayOffReason}
              onChange={(event) => setDayOffReason(event.target.value)}
              sx={dayOffFieldSx}
            />
            <Button
              id="booster-day-off-add"
              variant="contained"
              size="md"
              hierarchy="Secondary gray"
              disabled={!dayOff}
              onClick={() => {
                dispatch(
                  addBlackout({
                    startDate: dayOff,
                    endDate: dayOff,
                    reason: dayOffReason || undefined,
                  }),
                );
                setDayOff('');
                setDayOffReason('');
              }}
            >
              {STRINGS.targets.addDayOff}
            </Button>
          </Box>
          {targets.blackouts.length > 0 && (
            <Box sx={stepChipRowSx}>
              {targets.blackouts.map((blackout) => (
                <Chip
                  key={blackout.id}
                  id={`booster-blackout-${blackout.id}`}
                  size="small"
                  label={
                    blackout.reason
                      ? `${longDate(blackout.startDate)} · ${blackout.reason}`
                      : longDate(blackout.startDate)
                  }
                  onDelete={() => dispatch(removeBlackout(blackout.id))}
                  deleteIcon={
                    <Box component="span" sx={deleteIconSx}>
                      <AppIcon iconName="XClose" diameter="12px" color="Gray/500" />
                    </Box>
                  }
                  sx={blackoutChipSx}
                />
              ))}
            </Box>
          )}
        </SectionGroup>
      </SectionCard>

      <InsufficientScheduleCallout
        reasons={insufficientReasons}
        id="booster-targets-insufficient-schedule-callout"
      />

      {coverage && (
        <Box sx={bannerSx} id="booster-plan-coverage-banner">
          <BadgeCircle size="md" />
          <Typography variant="text-sm-regular" sx={bannerTextSx}>
            {STRINGS.targets.coverageBanner(coverage.weeks, coverage.sessions)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const dayPickerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const dayToggleSx =
  (selected: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '40px',
    height: '40px',
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: backrs.radius['radius-full'],
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0,
    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
    border: `1px solid ${
      selected ? backrs.colors['Colors/Base/Black'] : backrs.colors['Colors/Border/border-primary']
    }`,
    backgroundColor: selected
      ? backrs.colors['Colors/Background/bg-brand-solid']
      : backrs.colors['Colors/Background/bg-primary'],
    color: selected
      ? backrs.colors['Colors/Text/text-white']
      : backrs.colors['Colors/Text/text-secondary'],
    '&:hover': {
      backgroundColor: selected
        ? backrs.colors['Colors/Background/bg-brand-solid-hover']
        : backrs.colors['Colors/Background/bg-primary-hover'],
    },
  });

const dateChipSx: SxProps<Theme> = ({ backrs }) => ({
  height: 'auto',
  padding: `${backrs.spacing['spacing-xxs']} ${backrs.spacing['spacing-md']}`,
  borderRadius: backrs.radius['radius-full'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-secondary-hover'],
  },
  '& .MuiChip-label': {
    padding: 0,
  },
});

const sliderRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-2xl'],
  maxWidth: backrs.width['width-sm'],
});

const sliderSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Background/bg-brand-solid'],
  '& .MuiSlider-rail': {
    backgroundColor: backrs.colors['Colors/Border/border-secondary'],
    opacity: 1,
  },
  '& .MuiSlider-thumb': {
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    boxShadow: 'none',
    '&:hover, &.Mui-focusVisible, &.Mui-active': {
      boxShadow: 'none',
    },
  },
});

const hoursLabelSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: '64px',
  color: backrs.colors['Colors/Text/text-primary'],
});

const dayOffRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  maxWidth: backrs.width['width-lg'],
});

// Needs a real basis — `flex: 1` sets basis 0, and the fields never trigger their own wrap.
const dayOffFieldSx: SxProps<Theme> = {
  flex: '1 1 220px',
  minWidth: 0,
};

const blackoutChipSx: SxProps<Theme> = ({ backrs }) => ({
  height: 'auto',
  padding: `${backrs.spacing['spacing-xxs']} ${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-xxs']} 10px`,
  gap: backrs.spacing['spacing-xxs'],
  borderRadius: backrs.radius['radius-full'],
  border: `1px solid ${backrs.colors['Gray/200']}`,
  backgroundColor: backrs.colors['Gray/50'],
  '& .MuiChip-label': { padding: 0 },
  '& .MuiChip-deleteIcon': { margin: 0 },
});

const deleteIconSx: SxProps<Theme> = {
  display: 'flex',
  cursor: 'pointer',
};

const bannerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-xs'],
  alignItems: 'baseline',
  padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-xl']}`,
  borderRadius: backrs.radius['radius-md'],
  borderTopLeftRadius: 0,
  border: `1px solid ${backrs.colors['Beige/950']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const bannerTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
